import { FolderItem } from '../Email.interface';
import { getInboxFolder } from '../Email.utils';

/**
 * A single node in the folder tree built from the flat mail folder list.
 * Replaces Fluent UI v0 `TreeItemProps` with a library-agnostic shape.
 */
export interface MailFolderTreeItem {
  id: string;
  parent?: string | null;
  title: string;
  items: MailFolderTreeItem[];
}

const mailFolderToTreeItem = (mailFolders: FolderItem[]): MailFolderTreeItem[] =>
  mailFolders.map(
    (folder) =>
      ({
        id: folder.id,
        parent: folder.parentFolderId,
        items: [],
        title: folder.wellKnownName,
      } as MailFolderTreeItem),
  );

export const transformMailFolderListToTree = (
  folders: FolderItem[],
): MailFolderTreeItem[] => {
  if (folders.length === 0) {
    return [];
  }
  const visible = folders.filter((folder) => !folder.isHidden);

  // Inbox has to be on the top level, since we do not receive the
  // root folder id in the mailFolders graph call, we try to get
  // the root folder id here.
  const inbox = getInboxFolder(visible);

  if (inbox === null) {
    return [];
  }

  const rootId = inbox.parentFolderId;

  const treeItems = mailFolderToTreeItem(folders);
  const root: MailFolderTreeItem[] = [];
  const map: { [key: string]: number } = {};

  for (let i = 0; i < folders.length; ++i) {
    map[treeItems[i].id] = i;
    treeItems[i].items = [];
  }

  for (let i = 0; i < treeItems.length; ++i) {
    const node = treeItems[i];
    if (node.parent !== rootId) {
      const parentKey = node.parent ?? '';
      const parentIndex = map[parentKey];
      if (
        parentIndex !== undefined &&
        treeItems[parentIndex] &&
        treeItems[parentIndex].items
      ) {
        treeItems[parentIndex].items.push(node);
      }
    } else {
      root.push(node);
    }
  }

  return root;
};
